import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

interface User {
  id: string;
  email: string;
  displayName: string;
  role: 'viewer' | 'editor' | 'admin';
  isActive: boolean;
  emailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
}

async function fetchUsers(search?: string): Promise<User[]> {
  const token = useAuthStore.getState().accessToken;
  const params = search ? `?search=${encodeURIComponent(search)}` : '';
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/users${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  if (!response.ok) throw new Error('Failed to fetch users');
  return response.json();
}

async function changeRole(userId: string, role: string) {
  const token = useAuthStore.getState().accessToken;
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/users/${userId}/role`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ role }),
  });
  if (!response.ok) throw new Error('Failed to change role');
  return response.json();
}

async function toggleActive(userId: string, activate: boolean) {
  const token = useAuthStore.getState().accessToken;
  const endpoint = activate ? 'activate' : 'deactivate';
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/users/${userId}/${endpoint}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  if (!response.ok) throw new Error('Failed to toggle active status');
  return response.json();
}

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  // Fetch all users
  const { data: users, isLoading } = useQuery<User[]>({
    queryKey: ['admin-users', search],
    queryFn: () => fetchUsers(search),
  });

  // Change role mutation
  const changeRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => changeRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  // Activate/deactivate mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ userId, activate }: { userId: string; activate: boolean }) => toggleActive(userId, activate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'editor':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-6xl py-8">
        <div className="flex items-center justify-center p-8">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-6xl py-8">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Brukeradministrasjon</h1>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <div className="p-4">
          <Input
            placeholder="Søk etter brukere (e-post eller navn)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full"
          />
        </div>
      </Card>

      {/* Users Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Bruker
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Rolle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Verifisert
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Sist innlogget
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {users?.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{user.displayName}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={user.role}
                      onChange={(e) =>
                        changeRoleMutation.mutate({
                          userId: user.id,
                          role: e.target.value,
                        })
                      }
                      className={`rounded-full px-3 py-1 text-xs font-medium ${getRoleBadgeClass(
                        user.role
                      )}`}
                    >
                      <option value="viewer">Leser</option>
                      <option value="editor">Redaktør</option>
                      <option value="admin">Administrator</option>
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                        user.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {user.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {user.emailVerified ? (
                      <span className="text-green-600">✓</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {user.lastLoginAt
                      ? new Date(user.lastLoginAt).toLocaleDateString('nb-NO')
                      : '—'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        toggleActiveMutation.mutate({
                          userId: user.id,
                          activate: !user.isActive,
                        })
                      }
                      disabled={toggleActiveMutation.isPending}
                    >
                      {user.isActive ? 'Deaktiver' : 'Aktiver'}
                    </Button>
                  </td>
                </tr>
              ))}
              {(!users || users.length === 0) && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    {search ? 'Ingen brukere funnet' : 'Ingen brukere ennå'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {users && users.length > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          Viser {users.length} bruker{users.length !== 1 ? 'e' : ''}
        </div>
      )}
    </div>
  );
}
